import { useMemo } from 'react';
import { evaluateCapstone } from '../../content/lessons/helpers';
import { CAPSTONE_CRITERIA } from '../../content/presets';
import { metricText, summarizeRun, type RunMetrics } from '../../simulation/metrics';
import { analyzeDamping } from '../../simulation/model';
import type { ExperimentConfig, RunStats, Sample } from '../../simulation/types';
import { compactTrace, expandTrace, type RunRecord } from '../../state/runRecord';
import { Term } from '../Term';

const CAPSTONE_PRESETS = new Set(['capstone', 'capstone-transfer', 'capstone-free']);

export function metricsFor(trace: Sample[]): RunMetrics {
  return summarizeRun(trace, { tolerance: 0.05, finalWindow: 2 });
}

function evaluationNote(config: ExperimentConfig): string {
  const step = config.reference.steps[0];
  if (step) return `Rise, overshoot and settling are measured from the target change at ${step.t} s (tolerance ±0.05 m).`;
  return 'No target change in this run: rise time and overshoot do not apply; settling is measured from t = 0 around the target (±0.05 m).';
}

export function MetricRows({ m, stats, kind }: { m: RunMetrics; stats: RunStats; kind: string }) {
  return (
    <>
      <tr>
        <th scope="row">
          <Term id="rise-time">Rise time</Term>
        </th>
        <td>{metricText(m.riseTime, 's')}</td>
      </tr>
      <tr>
        <th scope="row">
          <Term id="overshoot">Overshoot</Term>
        </th>
        <td>{m.overshoot.status === 'ok' ? `${(m.overshoot.value! * 100).toFixed(1)} % of step` : metricText(m.overshoot, '')}</td>
      </tr>
      <tr>
        <th scope="row">
          <Term id="settling-time">Settling time</Term>
        </th>
        <td>{metricText(m.settlingTime, 's')}</td>
      </tr>
      <tr>
        <th scope="row">
          Final 2 s mean <Term id="error">error</Term>
        </th>
        <td>{metricText(m.finalMeanError, 'm', 4)}</td>
      </tr>
      <tr>
        <th scope="row">
          Time at <Term id="saturation">saturation</Term>
        </th>
        <td>{stats.saturationTime.toFixed(2)} s</td>
      </tr>
      <tr>
        <th scope="row">
          <Term id="iae">Accumulated error magnitude</Term>
        </th>
        <td>{stats.iae.toFixed(3)} m·s</td>
      </tr>
      {kind === 'bang-bang' && (
        <tr>
          <th scope="row">Thrust switches</th>
          <td>{stats.switchCount}</td>
        </tr>
      )}
    </>
  );
}

export function CapstoneCheck({ record }: { record: RunRecord }) {
  const res = evaluateCapstone(record);
  const C = CAPSTONE_CRITERIA;
  const rows = [
    { label: `Overshoot < ${C.maxOvershoot * 100} %`, ok: res.overshoot !== null && res.overshoot < C.maxOvershoot, value: res.overshoot === null ? 'n/a' : `${(res.overshoot * 100).toFixed(1)} %` },
    { label: `Settled after step within ${C.settleWithin} s`, ok: res.settleStep !== null && res.settleStep <= C.settleWithin, value: res.settleStep === null ? 'not settled' : `${res.settleStep.toFixed(2)} s` },
    { label: `Settled after load within ${C.settleWithin} s`, ok: res.settleDisturbance !== null && res.settleDisturbance <= C.settleWithin, value: res.settleDisturbance === null ? 'not settled' : `${res.settleDisturbance.toFixed(2)} s` },
    { label: `Saturation ≤ ${C.maxSaturation} s`, ok: res.saturation <= C.maxSaturation, value: `${res.saturation.toFixed(2)} s` },
  ];
  return (
    <div className={`criteria ${res.pass ? 'pass' : 'fail'}`}>
      <h4>Capstone criteria: {res.pass ? 'all met ✓' : 'not yet met'}</h4>
      <ul>
        {rows.map((r) => (
          <li key={r.label}>
            <span aria-hidden="true">{r.ok ? '✓' : '✗'}</span> <span className="sr-only">{r.ok ? 'Met:' : 'Not met:'}</span> {r.label}. {r.value}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DampingInfo({ config }: { config: ExperimentConfig }) {
  const a = analyzeDamping(config);
  return (
    <details className="damping">
      <summary>
        Damping analysis:{' '}
        {a.zeta === null ? 'not defined' : `ζ = ${a.zeta.toFixed(2)} (${a.regime})${a.applicable ? '' : '. See assumptions'}`}
      </summary>
      <p className="small">
        Effective stiffness k + K_p = {a.effectiveStiffness.toFixed(2)} N/m; effective damping b + K_d = {a.effectiveDamping.toFixed(2)} N·s/m
        {a.wn !== null && <>; ω_n = {a.wn.toFixed(2)} rad/s</>}.
      </p>
      <ul className="small">
        {a.notes.map((n) => (
          <li key={n}>{n}</li>
        ))}
      </ul>
    </details>
  );
}

interface Props {
  config: ExperimentConfig;
  presetId: string;
  trace: Sample[];
  traceVersion: number;
  stats: RunStats | null;
  done: boolean;
  status: string;
  showDamping: boolean;
}

export function MetricsPanel({ config, presetId, trace, traceVersion, stats, done, status, showDamping }: Props) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const m = useMemo(() => (done ? metricsFor(trace) : null), [done, traceVersion]);
  const record = useMemo(() => {
    if (!done || !stats || !CAPSTONE_PRESETS.has(presetId)) return null;
    return {
      config,
      status: status === 'complete' ? 'complete' : 'stopped',
      stats,
      trace: compactTrace(trace, 1),
    } as unknown as RunRecord;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, traceVersion, presetId]);
  return (
    <section className="metrics" aria-label="Run metrics">
      <h3>Metrics</h3>
      {done && m && stats && (
        <>
          <table className="kv">
            <tbody>
              <MetricRows m={m} stats={stats} kind={config.controller.kind} />
            </tbody>
          </table>
          <p className="hint">{evaluationNote(config)} Metrics use the true altitude.</p>
        </>
      )}
      {record && <CapstoneCheck record={record} />}
      {showDamping && <DampingInfo config={config} />}
    </section>
  );
}

export function runMetrics(r: RunRecord): RunMetrics {
  return metricsFor(expandTrace(r.trace));
}
